import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const SavedSearchesPage = () => {
  const { getAuthHeader } = useAuth();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newSearch, setNewSearch] = useState({
    search_name: '',
    category: '',
    min_price: '',
    max_price: '',
    location: ''
  });

  useEffect(() => {
    loadSavedSearches();
  }, []);

  const loadSavedSearches = async () => {
    setLoading(true);
    try {
      const response = await api.getSavedSearches(getAuthHeader());
      setSearches(response.data);
    } catch (error) {
      console.error('Error loading saved searches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSearch = async (e) => {
    e.preventDefault();

    const criteria = {};
    if (newSearch.category) criteria.category = newSearch.category;
    if (newSearch.min_price) criteria.min_price = parseFloat(newSearch.min_price);
    if (newSearch.max_price) criteria.max_price = parseFloat(newSearch.max_price);
    if (newSearch.location) criteria.location = newSearch.location;

    try {
      await api.createSavedSearch(
        { search_name: newSearch.search_name, criteria },
        getAuthHeader()
      );
      toast.success('Search saved!');
      setShowDialog(false);
      setNewSearch({ search_name: '', category: '', min_price: '', max_price: '', location: '' });
      loadSavedSearches();
    } catch (error) {
      toast.error('Failed to save search');
    }
  };

  const handleDeleteSearch = async (searchId) => {
    try {
      await api.deleteSavedSearch(searchId, getAuthHeader());
      toast.success('Search deleted');
      loadSavedSearches();
    } catch (error) {
      toast.error('Failed to delete search');
    }
  };

  return (
    <div data-testid="saved-searches-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold font-heading">
          Saved Searches ({searches.length})
        </h2>
        
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-search-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create New Search
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Saved Search</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSearch} className="space-y-4">
              <div>
                <Label htmlFor="search_name">Search Name</Label>
                <Input
                  id="search_name"
                  value={newSearch.search_name}
                  onChange={(e) => setNewSearch({ ...newSearch, search_name: e.target.value })}
                  required
                  data-testid="search-name-input"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={newSearch.category} 
                  onValueChange={(value) => setNewSearch({ ...newSearch, category: value })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Categories</SelectItem>
                    <SelectItem value="Residential">Residential</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_price">Min Price</Label>
                  <Input
                    id="min_price"
                    type="number"
                    value={newSearch.min_price}
                    onChange={(e) => setNewSearch({ ...newSearch, min_price: e.target.value })}
                    data-testid="min-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max_price">Max Price</Label>
                  <Input
                    id="max_price"
                    type="number"
                    value={newSearch.max_price}
                    onChange={(e) => setNewSearch({ ...newSearch, max_price: e.target.value })}
                    data-testid="max-price-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={newSearch.location}
                  onChange={(e) => setNewSearch({ ...newSearch, location: e.target.value })}
                  data-testid="location-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="save-search-btn">
                Save Search
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-slate-600">Loading saved searches...</p>
        </div>
      ) : searches.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-sm">
          <p className="text-slate-600">You haven't saved any searches yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {searches.map((search) => (
            <div 
              key={search.search_id} 
              className="p-6 bg-white border border-slate-200 rounded-sm"
              data-testid={`saved-search-${search.search_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-semibold text-lg">{search.search_name}</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteSearch(search.search_id)}
                  data-testid={`delete-search-${search.search_id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                {search.criteria.category && (
                  <p><span className="font-medium">Category:</span> {search.criteria.category}</p>
                )}
                {search.criteria.min_price && (
                  <p><span className="font-medium">Min Price:</span> £{search.criteria.min_price.toLocaleString()}</p>
                )}
                {search.criteria.max_price && (
                  <p><span className="font-medium">Max Price:</span> £{search.criteria.max_price.toLocaleString()}</p>
                )}
                {search.criteria.location && (
                  <p><span className="font-medium">Location:</span> {search.criteria.location}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedSearchesPage;